import {
  AuthLaunchDarklyProvider,
  type LaunchDarklyAuthState,
  useLaunchDarklyStatus,
} from "@assetwatch/auth";
import { useRouterState } from "@tanstack/react-router";
import type { LDContext } from "launchdarkly-react-client-sdk";
import type { ReactNode } from "react";
import { useResolvedEnterpriseCustomer } from "../../hooks/useResolvedEnterpriseCustomer";

export { useLaunchDarklyStatus };

export default function LaunchDarklyProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { externalCustomerId: routeExternalCustomerId } = useRouterState({
    select: (state) =>
      (state.location.search as { externalCustomerId?: string } | undefined) ??
      {},
  });
  const {
    isInternalUser,
    hasResolvedExternalCustomer,
    isLoadingSelectableCustomers,
    resolvedExternalCustomerId,
  } = useResolvedEnterpriseCustomer(routeExternalCustomerId);
  const externalCustomerId =
    routeExternalCustomerId ?? resolvedExternalCustomerId;

  function getContext({ user }: LaunchDarklyAuthState): LDContext {
    const userContext = {
      kind: "user" as const,
      key: user?.sub ?? "anonymous",
      email: user?.email ?? "",
      role: user?.role ?? "",
      roles: user?.roles ?? [],
      cognitoGroups: user?.cognitoGroups ?? [],
      isInternalUser,
    };

    if (!externalCustomerId) {
      return userContext;
    }

    return {
      kind: "multi",
      user: userContext,
      customer: {
        kind: "customer",
        key: externalCustomerId,
        externalCustomerId,
      },
    };
  }

  function isReadyToIdentify({
    isAuthenticated,
    isLoading,
    user,
  }: LaunchDarklyAuthState) {
    return (
      !isLoading &&
      isAuthenticated &&
      Boolean(user?.sub) &&
      Boolean(user?.email) &&
      (isInternalUser ||
        hasResolvedExternalCustomer ||
        Boolean(routeExternalCustomerId) ||
        !isLoadingSelectableCustomers)
    );
  }

  return (
    <AuthLaunchDarklyProvider
      clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID}
      getContext={getContext}
      isReadyToIdentify={isReadyToIdentify}
    >
      {children}
    </AuthLaunchDarklyProvider>
  );
}
